/* Farmscraft homepage: sliders, product tabs, quick add-to-cart */
(() => {
  if (!customElements.get('fch-slider')) {
    class FchSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.fch-track');
        if (!this.track) return;
        this.prev = this.querySelector('.fch-nav--prev');
        this.next = this.querySelector('.fch-nav--next');
        this.bar = this.querySelector('.fch-progress__bar');
        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', this.update, { passive: true });
        if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
        if (this.next) this.next.addEventListener('click', () => this.go(1));
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(this.update);
          this.observer.observe(this.track);
        }
        this.update();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      go(direction) {
        const step = Math.max(this.track.clientWidth * 0.8, 200);
        this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      update() {
        const track = this.track;
        if (!track) return;
        const max = track.scrollWidth - track.clientWidth;
        const scrollable = max > 2;
        this.classList.toggle('is-static', !scrollable);
        if (this.prev) this.prev.disabled = !scrollable || track.scrollLeft <= 2;
        if (this.next) this.next.disabled = !scrollable || track.scrollLeft >= max - 2;
        if (this.bar && track.scrollWidth > 0) {
          const width = Math.max((track.clientWidth / track.scrollWidth) * 100, 8);
          const left = scrollable ? (track.scrollLeft / max) * (100 - width) : 0;
          this.bar.style.width = `${width}%`;
          this.bar.style.left = `${left}%`;
        }
      }
    }
    customElements.define('fch-slider', FchSlider);
  }

  if (!customElements.get('fch-tabs')) {
    class FchTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index));
          tab.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
            event.preventDefault();
            const offset = event.key === 'ArrowRight' ? 1 : -1;
            const next = (index + offset + this.tabs.length) % this.tabs.length;
            this.select(next);
            this.tabs[next].focus();
          });
        });
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (!panel) return;
          panel.hidden = !selected;
          if (selected) {
            const slider = panel.querySelector('fch-slider');
            if (slider && typeof slider.update === 'function') {
              slider.track.scrollLeft = 0;
              slider.update();
            }
          }
        });
      }
    }
    customElements.define('fch-tabs', FchTabs);
  }

  if (!customElements.get('fch-tour')) {
    // Live 360° tour in an iframe. A transparent shield sits on top so the page keeps
    // scrolling normally; the tour becomes interactive only after a click/tap.
    class FchTour extends HTMLElement {
      connectedCallback() {
        this.frame = this.querySelector('iframe');
        if (!this.frame || !this.dataset.src) return;
        this.shield = this.querySelector('.fch-tour__shield');
        this.exit = this.querySelector('.fch-tour__exit');
        this.usingTouch = false;

        this.frame.addEventListener('load', () => {
          if (this.frame.getAttribute('src')) this.classList.add('is-loaded');
        });
        if (this.shield) this.shield.addEventListener('click', () => this.activate());
        if (this.exit) this.exit.addEventListener('click', () => this.deactivate());
        this.addEventListener('touchstart', () => { this.usingTouch = true; }, { passive: true });
        this.addEventListener('mouseleave', () => { if (!this.usingTouch) this.deactivate(); });
        document.querySelectorAll(`[data-tour-start="${this.id}"]`).forEach((button) => {
          button.addEventListener('click', () => this.activate(true));
        });
        if ('IntersectionObserver' in window) {
          new IntersectionObserver((entries) => {
            entries.forEach((entry) => { if (!entry.isIntersecting) this.deactivate(); });
          }).observe(this);
        }

        // Load the heavy tour after the storefront has loaded, but never wait on slow
        // third-party assets for more than a few seconds after the DOM is ready.
        const start = () => {
          if (this.started) return;
          this.started = true;
          setTimeout(() => this.load(), 250);
        };
        if (document.readyState === 'complete') {
          start();
        } else {
          window.addEventListener('load', start, { once: true });
          const fallback = () => setTimeout(start, 2500);
          if (document.readyState === 'interactive') fallback();
          else document.addEventListener('DOMContentLoaded', fallback, { once: true });
        }
      }

      load() {
        if (!this.frame.getAttribute('src')) this.frame.setAttribute('src', this.dataset.src);
      }

      activate(fromButton) {
        this.load();
        this.classList.add('is-active');
        if (this.exit) this.exit.hidden = false;
        if (fromButton) {
          const rect = this.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        try { this.frame.focus(); } catch (e) { /* cross-origin focus can fail silently */ }
      }

      deactivate() {
        this.classList.remove('is-active');
        if (this.exit) this.exit.hidden = true;
      }
    }
    customElements.define('fch-tour', FchTour);
  }

  if (!customElements.get('fch-tour-float')) {
    // Floating 360° button: the tour loads only when the popup is first opened.
    class FchTourFloat extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.fch-tf__btn');
        this.dialog = this.querySelector('dialog');
        this.frame = this.dialog && this.dialog.querySelector('iframe');
        if (!this.button || !this.dialog || !this.frame || !this.dataset.src) return;
        this.button.addEventListener('click', () => this.open());
        this.dialog.querySelectorAll('[data-tf-close]').forEach((button) => {
          button.addEventListener('click', () => this.dialog.close());
        });
        // A click on the dialog element itself means the backdrop was clicked.
        this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
        this.dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('fch-tf-lock');
          this.button.focus();
        });
        this.frame.addEventListener('load', () => {
          if (this.frame.getAttribute('src')) this.dialog.classList.add('is-loaded');
        });
      }

      open() {
        if (typeof this.dialog.showModal !== 'function') {
          window.open(this.dataset.src, '_blank', 'noopener');
          return;
        }
        if (!this.frame.getAttribute('src')) this.frame.setAttribute('src', this.dataset.src);
        this.dialog.showModal();
        document.documentElement.classList.add('fch-tf-lock');
      }
    }
    customElements.define('fch-tour-float', FchTourFloat);
  }

  if (!customElements.get('fch-look')) {
    // "Shop the room": numbered hotspots that open a small card; list items highlight their dot.
    class FchLook extends HTMLElement {
      connectedCallback() {
        this.spots = Array.from(this.querySelectorAll('.fch-spot'));
        this.spots.forEach((spot) => {
          const dot = spot.querySelector('.fch-spot__dot');
          if (!dot) return;
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            const open = !spot.classList.contains('is-open');
            this.closeAll();
            spot.classList.toggle('is-open', open);
            dot.setAttribute('aria-expanded', open ? 'true' : 'false');
          });
        });
        this.querySelectorAll('[data-spot-link]').forEach((item) => {
          const spot = document.getElementById(item.dataset.spotLink);
          if (!spot) return;
          item.addEventListener('mouseenter', () => spot.classList.add('is-hot'));
          item.addEventListener('mouseleave', () => spot.classList.remove('is-hot'));
          item.addEventListener('focus', () => spot.classList.add('is-hot'));
          item.addEventListener('blur', () => spot.classList.remove('is-hot'));
        });
        this.onDocumentClick = (event) => { if (!this.contains(event.target)) this.closeAll(); };
        document.addEventListener('click', this.onDocumentClick);
        this.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closeAll(); });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      closeAll() {
        this.spots.forEach((spot) => {
          spot.classList.remove('is-open');
          const dot = spot.querySelector('.fch-spot__dot');
          if (dot) dot.setAttribute('aria-expanded', 'false');
        });
      }
    }
    customElements.define('fch-look', FchLook);
  }

  // Scroll reveal: fade sections and cards up as they enter the viewport.
  // Skipped in the theme editor, with reduced motion, and for anything already on screen.
  if (!window.fchRevealStarted) {
    window.fchRevealStarted = true;
    const canReveal = 'IntersectionObserver' in window
      && !(window.Shopify && window.Shopify.designMode)
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (canReveal) {
      const selectors = [
        '.fch__head', '.fch-mosaic__big', '.fch-mosaic__tiles > *', '.fch-track > li', '.fch-banner',
        '.fch-seo__title', '.fch-seo__body', '.fch-seo__links', '.fch-marquee', '.fch-woods__stage',
        '.fch-woods__list', '.fch-look__stage', '.fch-look__list > li',
        '.fcs-intro__quote', '.fcs-stats > li', '.fcs-chapter__media', '.fcs-chapter__text',
        '.fcs-values__head', '.fcs-values__grid > li', '.fcs-founder__media', '.fcs-founder__text',
      ];
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          observer.unobserve(el);
          el.classList.add('is-inview');
          const cleanup = (event) => {
            if (event.target !== el) return; // ignore transitions bubbling up from children
            el.removeEventListener('transitionend', cleanup);
            el.removeAttribute('data-reveal');
            el.classList.remove('is-inview');
            el.style.removeProperty('--reveal-delay');
          };
          el.addEventListener('transitionend', cleanup);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
      const viewport = window.innerHeight;
      document.querySelectorAll(selectors.map((s) => `.fch ${s}`).join(',')).forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < viewport * 0.92 && rect.bottom > 0) return;
        const index = Math.min(Array.prototype.indexOf.call(el.parentElement.children, el), 5);
        el.style.setProperty('--reveal-delay', `${index * 80}ms`);
        el.setAttribute('data-reveal', '');
        observer.observe(el);
      });
    }
  }

  // Count-up numbers ([data-count-to]) when they scroll into view.
  if (!window.fchCountStarted) {
    window.fchCountStarted = true;
    const counters = document.querySelectorAll('[data-count-to]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const run = (el) => {
      const target = parseFloat(el.dataset.countTo);
      if (Number.isNaN(target) || reduce) return;
      const start = performance.now();
      const duration = 1400;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = Math.round(target * eased).toLocaleString('en-IN');
        if (t < 1) requestAnimationFrame(tick);
      };
      el.textContent = '0';
      requestAnimationFrame(tick);
    };
    if (counters.length && 'IntersectionObserver' in window) {
      const counterObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          counterObserver.unobserve(entry.target);
          run(entry.target);
        });
      }, { threshold: 0.6 });
      counters.forEach((el) => counterObserver.observe(el));
    }
  }

  if (!customElements.get('fch-quick-add')) {
    class FchQuickAdd extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
      }

      onSubmit(event) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        // Without the theme cart UI, let the form post normally to /cart/add.
        if (!cart || typeof cart.renderContents !== 'function' || !window.fetch) return;
        event.preventDefault();

        const button = this.form.querySelector('[type="submit"]');
        const message = this.querySelector('.fch-prod__msg');
        const label = button.querySelector('.fch-atc__label');
        const defaultLabel = label ? label.textContent : '';
        const setLabel = (text) => { if (label) label.textContent = text; };
        if (button.getAttribute('aria-busy') === 'true') return;
        button.setAttribute('aria-busy', 'true');
        setLabel('Adding…');
        if (message) message.hidden = true;

        const body = new FormData(this.form);
        body.append('sections', cart.getSectionsToRender().map((section) => section.id));
        body.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);

        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              setLabel(defaultLabel);
              if (message) {
                message.textContent = response.description || response.message || 'Could not add to cart.';
                message.hidden = false;
              }
              return;
            }
            setLabel('Added ✓');
            button.classList.add('is-added');
            setTimeout(() => {
              button.classList.remove('is-added');
              setLabel(defaultLabel);
            }, 1800);
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            cart.renderContents(response);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'fch-quick-add',
                productVariantId: body.get('id'),
                cartData: response,
              });
            }
          })
          .catch(() => {
            setLabel(defaultLabel);
            this.form.submit();
          })
          .finally(() => button.removeAttribute('aria-busy'));
      }
    }
    customElements.define('fch-quick-add', FchQuickAdd);
  }
})();
